import { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import useApiCall from '../../../../hooks/useApiCall';
import useGetGeneralAppData from '../../../../hooks/useGetGeneralAppData';
import ConsultsService from '../../../../services/ConsultsService';
import formatDateString from '../../../../utils/formatDateString';
import { ArquivoBase, BaseDashDataType, DadosNotImpl, IDashImpAutData, PeriodType, PreAdmDashDataType, SelectedDashboardType } from '../types';

export default function useConsultsDashboard() {
  const [isLoading, setIsLoading] = useState(false);
  const [selectedDashboard, setSelectedDashboard] = useState<SelectedDashboardType>('preAdm');
  const [changePeriodModalShow, setChangePeriodModalShow] = useState(false);
  const [finishProcessModalShow, setFinishProcessModalShow] = useState(false);
  const [downloadPreAdmOtimizationModalShow, setDownloadPreAdmOtimizationModalShow] = useState(false);
  const [downloadOtimizationBaseModalShow, setDownloadOtimizationBaseModalShow] = useState(false);
  const [finishCheckingModalShow, setFinishCheckingModalShow] = useState(false);

  const [hasPreAdmDashError, setHasPreAdmDashError] = useState(false);
  const [periodBeingSelected, setPeriodBeingSelected] = useState({
    month: (new Date()).getMonth() + 1,
    year: (new Date()).getFullYear(),
  } as PeriodType);
  const [selectedPeriod, setSelectedPeriod] = useState({
    month: (new Date()).getMonth() + 1,
    year: (new Date()).getFullYear(),
  } as PeriodType);
  const [periodBeingSelectedAutImp, setPeriodBeingSelectedAutImp] = useState({
    month: (new Date()).getMonth() + 1,
    year: (new Date()).getFullYear(),
  } as PeriodType);
  const [selectedPeriodAutImp, setSelectedPeriodAutImp] = useState({
    month: (new Date()).getMonth() + 1,
    year: (new Date()).getFullYear(),
  } as PeriodType);

  const [preAdmDashboardData, setPreAdmDashoardData] = useState({} as PreAdmDashDataType);
  const [baseDashData, setBaseDashData] = useState({} as BaseDashDataType);
  const [autImpData, setAutImpData] = useState([] as DadosNotImpl[]);

  const { userProfile, companyCode, userCode, userName } = useGetGeneralAppData();

  function handleSelectedDashboardChange(selectedDash: SelectedDashboardType) {
    setSelectedDashboard(selectedDash);
  }

  const { apiCall } = useApiCall();

  const loadImpAutData = useCallback(async (month?: number, year?: number) => {
    await apiCall({
      apiToCall: selectedDashboard === 'base' ? ConsultsService.getBaseDashboardImpAutData : ConsultsService.getPreAdmDashboardImpAutData,
      queryParams: {
        codUsu: userCode,
        perfilUsu: userProfile,
        codEmpresa: companyCode,
        mes: month || selectedPeriod.month,
        ano: year || selectedPeriod.year,
      },
      onStartLoad: () => setIsLoading(true),
      onEndLoad: () => setIsLoading(false),
      catchMessage: 'Não foi possível carregar o dashboard. Por favor, tente novamente',
      catchAction: () => setAutImpData([] as DadosNotImpl[]),
      actionAfterResponse: (response: IDashImpAutData) => {
        if (!response.codigo && !response.codRetorno) {
          toast.error(`Não foi possível carregar o dashboard. Por favor, tente novamente (${response.msg})`);
          setAutImpData([] as DadosNotImpl[]);
          return;
        }
        setAutImpData(response.dadosNotImpl);
        setPeriodBeingSelectedAutImp({ month: month || selectedPeriod.month, year: year || selectedPeriod.year });
        setSelectedPeriodAutImp({ month: month || selectedPeriod.month, year: year || selectedPeriod.year });
      },
    });
  }, [apiCall, companyCode, selectedDashboard, selectedPeriod.month, selectedPeriod.year, userCode, userProfile]);

  const loadPreAdmDashData = useCallback(async () => {
    if (selectedDashboard !== 'preAdm') return;
    await apiCall({
      apiToCall: ConsultsService.getPreAdmDashboardData,
      queryParams: {
        codUsu: userCode,
        perfilUsu: userProfile,
        codEmpresa: companyCode,
        mes: selectedPeriod.month,
        ano: selectedPeriod.year,
      },
      onStartLoad: () => setIsLoading(true),
      onEndLoad: () => setIsLoading(false),
      catchMessage: 'Não foi possível carregar o dashboard. Por favor, tente novamente',
      catchAction: () => setPreAdmDashoardData({} as PreAdmDashDataType),
      actionAfterResponse: (response: PreAdmDashDataType) => {
        if (!response.codigo) {
          toast.error(`Não foi possível carregar o dashboard. Por favor, tente novamente (${response.msg})`);
          setPreAdmDashoardData({} as PreAdmDashDataType);
          return;
        }
        setHasPreAdmDashError(false);
        setPreAdmDashoardData(response);
      },
    });

    await loadImpAutData();
  }, [apiCall, companyCode, loadImpAutData, selectedDashboard, selectedPeriod.month, selectedPeriod.year, userCode, userProfile]);

  const loadBaseDashData = useCallback(async () => {
    if (selectedDashboard !== 'base') return;
    await apiCall({
      apiToCall: ConsultsService.getBaseDashboardData,
      queryParams: {
        codUsu: userCode,
        perfilUsu: userProfile,
        codEmpresa: companyCode,
        mes: selectedPeriod.month,
        ano: selectedPeriod.year,
      },
      onStartLoad: () => setIsLoading(true),
      onEndLoad: () => setIsLoading(false),
      catchMessage: 'Não foi possível carregar o dashboard. Por favor, tente novamente',
      catchAction: () => setBaseDashData({} as BaseDashDataType),
      actionAfterResponse: (response: BaseDashDataType) => {
        if (!response.codigo || !response.dadosDashBase) {
          toast.error(`Não foi possível carregar o dashboard. Por favor, tente novamente (${response.msg})`);
          setBaseDashData({} as BaseDashDataType);
          return;
        }
        console.log(response);
        response.dadosDashBase.dataConferenciaProces = formatDateString(response.dadosDashBase.dataConferenciaProces);
        console.log('DATA CONFERENCIA', response.dadosDashBase.dataConferenciaProces);
        setBaseDashData(response);
      },
    });

    await loadImpAutData();
  }, [apiCall, companyCode, loadImpAutData, selectedDashboard, selectedPeriod.month, selectedPeriod.year, userCode, userProfile]);

  const finishProcess = useCallback(async (baseFiles: ArquivoBase[]) => {
    await apiCall({
      apiToCall: ConsultsService.finishBaseProcess,
      onStartLoad: () => setIsLoading(true),
      onEndLoad: () => setIsLoading(false),
      reqBody: JSON.stringify({
        codUsu: userCode,
        perfilUsu: userProfile,
        codEmpresa: companyCode,
        usuario: userName,
        tipoFinalizacao: 0,
        lotes: baseFiles.map((x) => ({
          lote: x.codLote,
        })),
      }),
      actionAfterResponse: async (response: { codigo: number, msg: string }) => {
        if (!response.codigo) {
          toast.error(`Não foi possível finalizar o processo. Por favor, tente novamente (${response.msg})`);
          return;
        }
        await loadBaseDashData();
        setFinishProcessModalShow(false);
        toast.success('Processo finalizado com sucesso!');
      },
      catchMessage: 'Não foi possível finalizar o processo. Por favor, tente novamente',
    });
  }, [apiCall, companyCode, loadBaseDashData, userCode, userName, userProfile]);

  const finishChecking = useCallback(async (baseFiles: ArquivoBase[]) => {
    await apiCall({
      apiToCall: ConsultsService.finishBaseProcess,
      onStartLoad: () => setIsLoading(true),
      onEndLoad: () => setIsLoading(false),
      reqBody: JSON.stringify({
        codUsu: userCode,
        perfilUsu: userProfile,
        codEmpresa: companyCode,
        usuario: userName,
        tipoFinalizacao: 1,
        lotes: baseFiles.map((x) => ({
          lote: x.codLote,
        })),
      }),
      actionAfterResponse: async (response: { codigo: number, msg: string }) => {
        if (!response.codigo) {
          toast.error(`Não foi possível finalizar o processo. Por favor, tente novamente (${response.msg})`);
          return;
        }
        await loadBaseDashData();
        setFinishCheckingModalShow(false);
        toast.success('Processo finalizado com sucesso!');
      },
      catchMessage: 'Não foi possível finalizar o processo. Por favor, tente novamente',
    });
  }, [apiCall, companyCode, loadBaseDashData, userCode, userName, userProfile]);

  const loadPage = useCallback(async () => {
    if (companyCode) {
      await Promise.all([
        loadPreAdmDashData(),
        loadBaseDashData(),
      ]);
    }
  }, [companyCode, loadBaseDashData, loadPreAdmDashData]);

  useEffect(() => {
    loadPage();
  }, [loadPage]);

  return {
    isLoading,
    setIsLoading,
    userProfile,
    selectedDashboard,
    handleSelectedDashboardChange,
    companyCode,
    hasPreAdmDashError,
    periodBeingSelected,
    setPeriodBeingSelected,
    selectedPeriod,
    setSelectedPeriod,
    preAdmDashboardData,
    changePeriodModalShow,
    setChangePeriodModalShow,
    baseDashData,
    finishProcessModalShow,
    setFinishProcessModalShow,
    finishProcess,
    downloadPreAdmOtimizationModalShow,
    setDownloadPreAdmOtimizationModalShow,
    downloadOtimizationBaseModalShow,
    setDownloadOtimizationBaseModalShow,
    finishChecking,
    setFinishCheckingModalShow,
    finishCheckingModalShow,
    autImpData,
    loadImpAutData,
    periodBeingSelectedAutImp,
    setPeriodBeingSelectedAutImp,
    selectedPeriodAutImp,
    setSelectedPeriodAutImp,
  };
}
